import {skipToken} from '@reduxjs/toolkit/query';
import {Button, Dropdown, DropdownGroup, DropdownItem, Separator} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotEmpty, isNotNil} from 'ramda';

import {
  InspectionType,
  useCreateInspectionMutation,
  useGetInspectionTemplatesQuery,
} from '@dms/api/core';
import {useGetServiceCaseTypeQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

interface CreateInspectionDropdownProps extends TestIdProps {
  vehicleId: string;
  serviceCaseTypeId: string;
  inspectionType: InspectionType.CHECKIN | InspectionType.HANDOVER;
  isDisabled?: boolean;
  isLoading?: boolean;
  onInspectionCreated: (inspectionId: string) => Promise<void>;
}

export function CreateInspectionDropdown(props: CreateInspectionDropdownProps) {
  const {data: inspectionTemplates} = useGetInspectionTemplatesQuery({isActive: true});
  const {data: serviceCaseType} = useGetServiceCaseTypeQuery(
    isNotEmpty(props.serviceCaseTypeId) ? {serviceCaseTypeId: props.serviceCaseTypeId} : skipToken
  );
  const [createInspection, {isLoading: isCreating}] = useCreateInspectionMutation();

  const defaultTemplateId = match<
    CreateInspectionDropdownProps['inspectionType'],
    string | undefined
  >(props.inspectionType)
    .with(InspectionType.CHECKIN, () => serviceCaseType?.defaultCheckInInspectionId)
    .with(InspectionType.HANDOVER, () => serviceCaseType?.defaultHandoverInspectionId)
    .exhaustive();

  const defaultTemplate = isNotNil(defaultTemplateId)
    ? (inspectionTemplates ?? []).find((t) => t.id === defaultTemplateId)
    : undefined;

  const otherTemplates = isNotNil(defaultTemplate)
    ? (inspectionTemplates ?? []).filter((t) => t.id !== defaultTemplateId)
    : (inspectionTemplates ?? []);

  const handleSelect = (templateId: string) => {
    createInspection({
      vehicleId: props.vehicleId,
      createAuditRequestBody: {
        inspectionType: props.inspectionType,
        auditStructureId: templateId,
      },
    })
      .unwrap()
      .then(({id}) => props.onInspectionCreated(id))
      .catch(handleApiError);
  };

  return (
    <Dropdown
      data-testid={props['data-testid']}
      placement="bottom-end"
      isHeightLimited
      isDisabled={props.isDisabled || props.isLoading || isCreating}
      dropdownControl={
        <Button
          variant="link"
          leftIcon="content/add_circle"
          title={i18n.t('general.labels.createNew')}
          isDisabled={props.isDisabled}
          isLoading={props.isLoading || isCreating}
        />
      }
    >
      <Show
        when={
          isNotNil(defaultTemplate) &&
          isNotEmpty(defaultTemplate?.name) &&
          isNotEmpty(defaultTemplate?.id)
        }
      >
        <DropdownGroup label={i18n.t('general.labels.default')}>
          <DropdownItem
            data-testid={suffixTestId('defaultTemplate', props)}
            label={defaultTemplate?.name ?? ''}
            onClick={() => handleSelect(defaultTemplate?.id ?? '')}
          />
        </DropdownGroup>
        <Separator spacing={1} />
      </Show>
      {otherTemplates.map((t, index) => (
        <DropdownItem
          key={t.id}
          data-testid={suffixTestId(`template-[${index}]`, props)}
          label={t.name}
          onClick={() => handleSelect(t.id)}
        />
      ))}
    </Dropdown>
  );
}
